import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Download, FileText } from 'lucide-react';
import { useAgreement } from '../context/AgreementContext';
import { SignatureCapture } from './SignatureCapture';
import { Signature, AgreementData, AgreementContextData, SMSFDetails, TrustDetails, CompanyDetails } from '../types';

// Define interface for signature data returned from the API
interface SignatureData {
  id: string;
  agreement_id: string;
  signature_url?: string;
  signed_at?: string;
  created_at: string;
  updated_at: string;
  order: number;
  has_signed: boolean;
}
import { scheduleItems, termsAndConditions } from '../data/agreementData';
import { getCustomScheduleItems, getCustomTermsAndConditions } from '../data/customAgreementData';
import { getFollowOnScheduleItems, getFollowOnTermsAndConditions } from '../data/followOnAgreementData';
import {
  shouldUseCustomAgreementDataFromContext,
  shouldUseFollowOnAgreementDataFromContext,
} from '../utils/agreementUtils';
import { getAgreementById, saveSignature, updateAgreementStatus, checkSignatureStatus, downloadAgreementPdf } from '../services/agreementService';

// Interface for secondary signer props
interface SecondarySignerProps {
  signerName: string;
  signatureData: string;
  signatureSubmitted: boolean;
  submitting: boolean;
  onSignatureChange: (signature: string) => void;
  onSubmitSignature: () => Promise<void>;
}

interface AgreementPreviewProps {
  onBack: () => void;
  isSecondaryView?: boolean;
  secondarySignerProps?: SecondarySignerProps;
  signatureId?: string; // Optional prop for signature ID from secondary signer view
}

export function AgreementPreview({
  onBack,
  isSecondaryView = false,
  secondarySignerProps,
  signatureId
}: AgreementPreviewProps) {
  const { state, dispatch } = useAgreement();
  const { agreement_id } = useParams<{ agreement_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [signatures, setSignatures] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [amlDisclaimerAccepted, setAmlDisclaimerAccepted] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [currentUserSigned, setCurrentUserSigned] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [agreementSigned, setAgreementSigned] = useState(false);
  const [agreementStatus, setAgreementStatus] = useState<string>('pending');
  const [shouldUseCustomData, setShouldUseCustomData] = useState(false);
  const [shouldUseFollowOnData, setShouldUseFollowOnData] = useState(false);
  const [agreementDataForCustomCheck, setAgreementDataForCustomCheck] = useState<{ id?: string } | null>(null);

  // Fetch agreement data when component mounts
  useEffect(() => {
    console.log('AgreementPreview - useEffect triggered with dependencies:', {
      agreement_id,
      currentStep: state.currentStep,
      preferencesMaxPrice: state.preferences?.maxPrice,
      signaturesLength: state.signatures?.length
    });
    
    const fetchAgreementData = async () => {
      if (!agreement_id) return;
      
      try {
        setLoading(true);
        
        // Fetch agreement data
        const agreementData = await getAgreementById(agreement_id);
        console.log('AgreementPreview - Fetched agreement data:', agreementData);
        
        // Set the agreement status
        setAgreementStatus(agreementData.status || 'pending');
        
        // Check if agreement is fully signed
        if (agreementData.status === 'signed') {
          setAgreementSigned(true);
          setCurrentUserSigned(true);
          setTermsAccepted(true);
          setAmlDisclaimerAccepted(true);
        }
        // Check if agreement is confirmed - set UI state but don't auto-redirect
        else if (agreementData.status === 'confirmed') {
          setAgreementSigned(true);
          setCurrentUserSigned(true);
          setTermsAccepted(true);
          setAmlDisclaimerAccepted(true);
        }
        // Check signature status on page refresh
        else if (agreementData.signatures && agreementData.signatures.length > 0) {
          try {
            // First check using the data from getAgreementById
            const currentUserSignature = agreementData.signatures.find((sig: SignatureData) => sig.order === 1);
            const allSignaturesComplete = agreementData.signatures.every((sig: SignatureData) => sig.has_signed);
            
            // Then make a specific call to check signature status from the database
            console.log('Checking signature status from database...');
            const signatureStatus = await checkSignatureStatus(agreement_id);
            
            console.log('Signature status from database:', signatureStatus);
            
            // Use the most up-to-date information from the dedicated endpoint
            if (signatureStatus.all_signatures_complete) {
              console.log('All signatures are complete (from status check)');
              setAgreementSigned(true);
              setCurrentUserSigned(true);
              setTermsAccepted(true);
              setAmlDisclaimerAccepted(true);
            } else if (signatureStatus.has_signed) {
              console.log('Current user has already signed (from status check), waiting for others');
              setCurrentUserSigned(true);
              setTermsAccepted(true);
              setAmlDisclaimerAccepted(true);
            } else if (allSignaturesComplete) {
              // Fallback to the agreement data if the status check doesn't indicate signing
              console.log('All signatures are complete (from agreement data)');
              setAgreementSigned(true);
              setCurrentUserSigned(true);
              setTermsAccepted(true);
              setAmlDisclaimerAccepted(true);
            } else if (currentUserSignature && currentUserSignature.has_signed) {
              console.log('Current user has already signed (from agreement data), waiting for others');
              setCurrentUserSigned(true);
              setTermsAccepted(true);
              setAmlDisclaimerAccepted(true);
            }
            
            console.log('Signature status on refresh:', {
              currentUserSignedFromDB: signatureStatus.has_signed,
              allSignaturesCompleteFromDB: signatureStatus.all_signatures_complete,
              currentUserSignedFromData: currentUserSignature?.has_signed,
              allSignaturesCompleteFromData: allSignaturesComplete,
              signatures: agreementData.signatures
            });
          } catch (error) {
            console.error('Error checking signature status:', error);
            
            // Fallback to using just the agreement data if the status check fails
            const currentUserSignature = agreementData.signatures.find((sig: SignatureData) => sig.order === 1);
            const allSignaturesComplete = agreementData.signatures.every((sig: SignatureData) => sig.has_signed);
            
            if (allSignaturesComplete) {
              console.log('All signatures are complete (fallback)');
              setAgreementSigned(true);
              setCurrentUserSigned(true);
              setTermsAccepted(true);
              setAmlDisclaimerAccepted(true);
            } else if (currentUserSignature && currentUserSignature.has_signed) {
              console.log('Current user has already signed (fallback), waiting for others');
              setCurrentUserSigned(true);
              setTermsAccepted(true);
              setAmlDisclaimerAccepted(true);
            }
          }
        }
        
        // If we have purchaser details in the agreement, update the context state
        if (agreementData.purchaser_details) {
          console.log('AgreementPreview - Purchaser details:', agreementData.purchaser_details);
          
          // Create a data structure that matches what the LOAD_AGREEMENT action expects
          const loadData: AgreementContextData = {
            id: agreementData.id,
            purchaserType: agreementData.purchaser_details.purchaserType || 'personal',
            partySize: agreementData.purchaser_details.individualDetails?.length || 1,
            preferences: agreementData.purchaser_details.agreementDetails?.preferences || state.preferences,
            individualDetails: agreementData.purchaser_details.individualDetails || [],
            signatures: state.signatures,
            currentStep: state.currentStep,
            isComplete: agreementData.is_draft === false,
            total_fee: agreementData.total_fee,
            total_retainer: agreementData.total_retainer
          };
          
          // Handle entity-specific details based on purchaser type
          if (agreementData.purchaser_details.purchaserType === 'smsf' && agreementData.purchaser_details.agreementDetails) {
            loadData.smsfDetails = {
              smsfName: agreementData.purchaser_details.agreementDetails.smsfName || '',
              corporateTrustee: agreementData.purchaser_details.agreementDetails.corporateTrustee || '',
              trustName: agreementData.purchaser_details.agreementDetails.trustName || '',
              directors: agreementData.purchaser_details.individualDetails || []
            } as SMSFDetails;
          } else if (agreementData.purchaser_details.purchaserType === 'trust' && agreementData.purchaser_details.agreementDetails) {
            loadData.trustDetails = {
              trustName: agreementData.purchaser_details.agreementDetails.trustName || '',
              directors: agreementData.purchaser_details.individualDetails || []
            } as TrustDetails;
          } else if (agreementData.purchaser_details.purchaserType === 'company' && agreementData.purchaser_details.agreementDetails) {
            loadData.companyDetails = {
              trustName: agreementData.purchaser_details.agreementDetails.trustName || '',
              corporateTrustee: agreementData.purchaser_details.agreementDetails.corporateTrustee || '',
              directors: agreementData.purchaser_details.individualDetails || []
            } as CompanyDetails;
          }
          
          // Load the agreement data into the context
          dispatch({ type: 'LOAD_AGREEMENT', payload: loadData });
        }
        
        // Store agreement data for custom data checking and check if we should use custom data
        setAgreementDataForCustomCheck({ id: agreementData.id });
        const useCustomData = shouldUseCustomAgreementDataFromContext(
          agreement_id,
          signatureId,
          { id: agreementData.id }
        );
        const useFollowOnData = shouldUseFollowOnAgreementDataFromContext(
          agreement_id,
          signatureId,
          { id: agreementData.id }
        );
        setShouldUseCustomData(useCustomData);
        setShouldUseFollowOnData(useFollowOnData);
        
        console.log('Custom data check:', {
          agreement_id,
          signatureId,
          agreementDataId: agreementData.id,
          shouldUseCustomData: useCustomData,
          shouldUseFollowOnData: useFollowOnData,
          isSecondaryView
        });
        
        setError(null);
      } catch (err) {
        console.error('Error fetching agreement data:', err);
        setError('Failed to load agreement data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAgreementData();
  }, [agreement_id, dispatch]); // Removed state dependencies that were causing infinite loop
  
  // Additional effect to handle custom data checking for secondary view or when we have signature ID but no direct agreement_id
  useEffect(() => {
    if (isSecondaryView && signatureId && state.id && !agreement_id) {
      // We're in secondary view and have the agreement ID from the context
      const useCustomData = shouldUseCustomAgreementDataFromContext(
        state.id,
        signatureId,
        { id: state.id }
      );
      const useFollowOnData = shouldUseFollowOnAgreementDataFromContext(
        state.id,
        signatureId,
        { id: state.id }
      );
      setShouldUseCustomData(useCustomData);
      setShouldUseFollowOnData(useFollowOnData);
      setAgreementDataForCustomCheck({ id: state.id });
      
      console.log('Secondary view custom data check:', {
        stateId: state.id,
        signatureId,
        shouldUseCustomData: useCustomData,
        shouldUseFollowOnData: useFollowOnData,
        isSecondaryView
      });
    }
  }, [isSecondaryView, signatureId, state.id, agreement_id]);
  
  // Replace placeholders in the agreement data with actual values
  const getProcessedScheduleItems = () => {
    // Check if fee data exists in agreement data
    const hasRetainerData = state.total_retainer !== undefined && state.total_retainer !== null;
    const hasSuccessFeeData = state.total_fee !== undefined && state.total_fee !== null;
    
    // Message to display when fee data is missing
    const consultantMessage = "**PLEASE CONTACT YOUR PROPERTY CONSULTANT FOR HELP**";
    
    // Extract base fees (excluding GST) from agreement data
    const retainerFeeExGST = hasRetainerData ? state.total_retainer! : 0;
    const totalFeeExGST = hasSuccessFeeData ? state.total_fee! : 0;
    
    // Calculate GST-inclusive amounts (multiply by 1.1 for 10% GST)
    const retainerAmount = hasRetainerData ? retainerFeeExGST * 1.1 : 0;
    const successFeeAmount = hasSuccessFeeData ? (totalFeeExGST - retainerFeeExGST) * 1.1 : 0;
    
    // Use follow-on, custom, or default schedule items based on agreement ID
    const itemsToProcess = shouldUseFollowOnData
      ? getFollowOnScheduleItems()
      : shouldUseCustomData
        ? getCustomScheduleItems()
        : scheduleItems;
    
    return itemsToProcess.map(item => {
      let processedContent = item.content;
      let hasDynamicContent = false;
      
      if (item.id === 3) {
        // Handle different purchaser types
        if (state.purchaserType === 'smsf' && state.smsfDetails) {
          // SMSF entity
          processedContent = processedContent
            .replace('means the following person(s):', 'means the following entity:')
            .replace('Name: [BUYER_NAME]', `Entity Name: **${state.smsfDetails.smsfName || 'N/A'}**`)
            .replace('\n\nAddress: [BUYER_ADDRESS]', '')
            .replace('\n\nEmail: [BUYER_EMAIL]', '')
            .replace('\n\nPhone number: [BUYER_PHONE]', '');
          
          // Add Corporate Trustee underneath Entity Name if it exists
          if (state.smsfDetails.corporateTrustee) {
            processedContent += `\nCorporate Trustee: **${state.smsfDetails.corporateTrustee}**`;
          }
          
          // Add directors underneath
          // For SMSF, all individualDetails are directors
          if (state.individualDetails && state.individualDetails.length > 0) {
            processedContent += '\n\n**Directors:**';
            state.individualDetails.forEach((director, index) => {
              processedContent += `\n\n**Director ${index + 1}**\nName: **${director.firstName} ${director.lastName}**\nAddress: **${director.addressLine1}, ${director.city}, ${director.state} ${director.postcode}**\nEmail: **${director.email}**\nPhone number: **${director.phone}**`;
            });
          }
          hasDynamicContent = true;
        } else if (state.purchaserType === 'trust' && state.trustDetails) {
          // Trust entity
          processedContent = processedContent
            .replace('means the following person(s):', 'means the following entity:')
            .replace('Name: [BUYER_NAME]', `Entity Name: **${state.trustDetails.trustName || 'N/A'}**`)
            .replace('\n\nAddress: [BUYER_ADDRESS]', '')
            .replace('\n\nEmail: [BUYER_EMAIL]', '')
            .replace('\n\nPhone number: [BUYER_PHONE]', '');
          
          // Add directors underneath
          // For Trust, all individualDetails are directors
          if (state.individualDetails && state.individualDetails.length > 0) {
            processedContent += '\n\n**Directors:**';
            state.individualDetails.forEach((director, index) => {
              processedContent += `\n\n**Director ${index + 1}**\nName: **${director.firstName} ${director.lastName}**\nAddress: **${director.addressLine1}, ${director.city}, ${director.state} ${director.postcode}**\nEmail: **${director.email}**\nPhone number: **${director.phone}**`;
            });
          }
          hasDynamicContent = true;
        } else if (state.purchaserType === 'company' && state.companyDetails) {
          // Company entity
          processedContent = processedContent
            .replace('means the following person(s):', 'means the following entity:')
            .replace('Name: [BUYER_NAME]', `Entity Name: **${state.companyDetails.trustName || 'N/A'}**`)
            .replace('\n\nAddress: [BUYER_ADDRESS]', '')
            .replace('\n\nEmail: [BUYER_EMAIL]', '')
            .replace('\n\nPhone number: [BUYER_PHONE]', '');
          
          // Add Corporate Trustee underneath Entity Name if it exists
          if (state.companyDetails.corporateTrustee) {
            processedContent += `\nCorporate Trustee: **${state.companyDetails.corporateTrustee}**`;
          }
          
          // Add directors underneath
          // For Company, all individualDetails are directors
          if (state.individualDetails && state.individualDetails.length > 0) {
            processedContent += '\n\n**Directors:**';
            state.individualDetails.forEach((director, index) => {
              processedContent += `\n\n**Director ${index + 1}**\nName: **${director.firstName} ${director.lastName}**\nAddress: **${director.addressLine1}, ${director.city}, ${director.state} ${director.postcode}**\nEmail: **${director.email}**\nPhone number: **${director.phone}**`;
            });
          }
          hasDynamicContent = true;
        } else {
          // Personal purchaser type
          const mainBuyer = state.individualDetails && state.individualDetails.length > 0 ? state.individualDetails[0] : null;
          if (mainBuyer) {
            processedContent = processedContent
              .replace('[BUYER_NAME]', `**${mainBuyer.firstName} ${mainBuyer.lastName}**`)
              .replace('[BUYER_ADDRESS]', `**${mainBuyer.addressLine1}, ${mainBuyer.city}, ${mainBuyer.state} ${mainBuyer.postcode}**`)
              .replace('[BUYER_EMAIL]', `**${mainBuyer.email}**`)
              .replace('[BUYER_PHONE]', `**${mainBuyer.phone}**`);
            
            // If there are additional people, list them underneath
            if (state.individualDetails && state.individualDetails.length > 1) {
              processedContent += '\n\n**Additional Parties:**';
              state.individualDetails.slice(1).forEach((person, index) => {
                processedContent += `\n\n**Person ${index + 2}**\nName: **${person.firstName} ${person.lastName}**\nAddress: **${person.addressLine1}, ${person.city}, ${person.state} ${person.postcode}**\nEmail: **${person.email}**\nPhone number: **${person.phone}**`;
              });
            }
            hasDynamicContent = true;
          }
        }
      } else if (item.id === 4) {
        // Replace retainer amount with bold formatting (includes GST) or consultant message
        if (hasRetainerData) {
          // Handle both standard pricing pattern and custom "$XX" placeholder
          processedContent = processedContent
            .replace(/\$[\d,]+/g, `**${formatPrice(retainerAmount)}**`)
            .replace(/\$XX/g, `**${formatPrice(retainerAmount)}**`);
        } else {
          processedContent = processedContent
            .replace(/\$[\d,]+/g, consultantMessage)
            .replace(/\$XX/g, consultantMessage);
        }
      } else if (item.id === 5) {
        // Replace success fee amount with bold formatting (includes GST) or consultant message
        if (hasSuccessFeeData) {
          // Handle both standard pricing pattern and custom "$XX" placeholder
          processedContent = processedContent
            .replace(/\$[\d,]+/g, `**${formatPrice(successFeeAmount)}**`)
            .replace(/\$XX/g, `**${formatPrice(successFeeAmount)}**`);
        } else {
          processedContent = processedContent
            .replace(/\$[\d,]+/g, consultantMessage)
            .replace(/\$XX/g, consultantMessage);
        }
      } else if (item.id === 6) {
        // Replace price limit with bold formatting
        const maxPrice = state.preferences?.maxPrice;
        if (maxPrice) {
          processedContent = processedContent.replace('[PRICE_LIMIT]', `**${formatPrice(maxPrice)}**`);
        } else {
          processedContent = processedContent.replace('[PRICE_LIMIT]', '**Contact Property Consultant**');
        }
        hasDynamicContent = true;
      }
      
      return {
        ...item,
        content: processedContent,
        hasDynamicContent
      };
    });
  };
  

  const getSigningParties = () => {
    const parties: string[] = [];
    
    // All individuals are stored in the individualDetails array
    // For all entity types, we use the entire individualDetails array
    if (state.individualDetails && state.individualDetails.length > 0) {
      state.individualDetails.forEach(detail => {
        if (detail.firstName && detail.lastName) {
          const fullName = [detail.firstName, detail.middleName, detail.lastName]
            .filter(Boolean)
            .join(' ');
          parties.push(fullName);
        }
      });
    }

    return parties;
  };

  const handleSignatureChange = (name: string, signatureData: string) => {
    setSignatures(prev => ({
      ...prev,
      [name]: signatureData
    }));
  };

  const areAllSigned = () => {
    if (!termsAccepted || !amlDisclaimerAccepted) return false;
    const parties = getSigningParties();
    // Only check if the first party (current user) has signed
    return parties.length > 0 && signatures[parties[0]] && signatures[parties[0]].trim() !== '';
  };

  const handleSubmit = async () => {
    if (!currentUserSigned) {
      // First click - sign the agreement
      if (!areAllSigned()) return;

      setIsSubmitting(true);

      try {
        // Get the current user's signature
        const currentUserName = signingParties[0];
        const signatureData = signatures[currentUserName];
        
        if (!signatureData) {
          throw new Error('No signature data found');
        }
        
        // Get the current user's ID
        const currentUser = state.individualDetails[0];
        if (!currentUser || !currentUser.id) {
          throw new Error('Current user information not found');
        }
        
        console.log('Saving signature for user:', currentUser.firstName, currentUser.lastName);
        
        // Save signature to backend
        const result = await saveSignature(
          agreement_id!,
          currentUser.id,
          signatureData
        );
        
        console.log('Signature save result:', result);
        
        // Update individual details with signature file info
        if (result && result.signature_url) {
          // Create a new sig_file array or use the existing one
          let updatedSigFiles = [];
          
          // If sig_file exists and is an array, use it as the base
          if (Array.isArray(currentUser.sig_file)) {
            updatedSigFiles = [...currentUser.sig_file];
            
            // Try to find and update an existing entry
            let found = false;
            for (let i = 0; i < updatedSigFiles.length; i++) {
              if (updatedSigFiles[i].id_type === "signature" &&
                  updatedSigFiles[i].sig_row_id === result.signature_id) {
                updatedSigFiles[i] = {
                  ...updatedSigFiles[i],
                  id_url: result.signature_url
                };
                found = true;
                break;
              }
            }
            
            // If no matching entry was found, add a new one
            if (!found) {
              updatedSigFiles.push({
                id_url: result.signature_url,
                id_type: 'signature',
                sig_row_id: result.signature_id
              });
            }
          } else {
            // If sig_file doesn't exist or isn't an array, create a new array with just this entry
            updatedSigFiles = [{
              id_url: result.signature_url,
              id_type: 'signature',
              sig_row_id: result.signature_id
            }];
          }
          
          dispatch({
            type: 'UPDATE_INDIVIDUAL_SIGNATURE',
            payload: {
              individualId: currentUser.id,
              signatureFile: updatedSigFiles
            }
          });
        }
        
        // Get the updated agreement status and signature data from the result
        const agreementStatus = result.agreement_status || 'pending';
        
        // Update UI based on signature status
        if (agreementStatus === 'signed') {
          // All signatures are complete
          console.log('All signatures complete, agreement fully signed');
          setAgreementSigned(true);
          setCurrentUserSigned(true);
        } else {
          // Current user has signed but we're waiting for others
          console.log('Current user signed, waiting for other signatures');
          setCurrentUserSigned(true);
          
          // Check signature status directly from the database
          try {
            console.log('Checking signature status from database after signing...');
            const signatureStatus = await checkSignatureStatus(agreement_id!);
            
            console.log('Signature status after signing:', signatureStatus);
            
            // Update UI based on the most accurate signature status
            if (signatureStatus.all_signatures_complete) {
              console.log('All signatures are complete based on status check');
              setAgreementSigned(true);
            }
            
            // Also refresh agreement data as a fallback
            const refreshedData = await getAgreementById(agreement_id!);
            
            // Check if all signatures are now complete (in case status wasn't updated properly)
            const allSignaturesComplete = refreshedData.signatures &&
              refreshedData.signatures.length > 0 &&
              refreshedData.signatures.every((sig: any) => sig.has_signed);
              
            if (allSignaturesComplete && !signatureStatus.all_signatures_complete) {
              console.log('All signatures are complete based on refreshed data (status check didn\'t catch this)');
              setAgreementSigned(true);
            }
          } catch (error) {
            console.error('Error checking signature status after signing:', error);
            
            // Fallback to just refreshing agreement data if status check fails
            const refreshedData = await getAgreementById(agreement_id!);
            
            // Check if all signatures are now complete
            const allSignaturesComplete = refreshedData.signatures &&
              refreshedData.signatures.length > 0 &&
              refreshedData.signatures.every((sig: any) => sig.has_signed);
              
            if (allSignaturesComplete) {
              console.log('All signatures are complete based on refreshed data (fallback)');
              setAgreementSigned(true);
            }
          }
        }
        
        // Convert signatures to the format expected by the context
        const signatureList: Signature[] = Object.entries(signatures).map(([name, signature], index) => ({
          id: result.signature_id || (Date.now().toString() + Math.random().toString(36).substr(2, 9)),
          order: index + 1,
          signature_data: signature,
          individual_id: state.individualDetails[0]?.id
        }));

        // Add signatures to context
        signatureList.forEach(signature => {
          dispatch({ type: 'ADD_SIGNATURE', payload: signature });
        });

        // Mark current user as signed
        setCurrentUserSigned(true);

      } catch (error) {
        console.error('Error submitting agreement:', error);
        // Show error to user
        setError(error instanceof Error ? error.message : 'Failed to save signature');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Second click - handle based on agreement status
      if (agreementSigned) {
        // Set loading state for the confirm button
        setIsConfirming(true);
        
        // Check current agreement status to determine action
        try {
          const currentAgreement = await getAgreementById(agreement_id!);
          
          if (currentAgreement.status === 'confirmed') {
            // Agreement is already confirmed, navigate directly to payment
            console.log('Agreement already confirmed, navigating to payment...');
            navigate(`/${agreement_id}/payment`);
          } else if (currentAgreement.status === 'signed') {
            // Agreement is signed but not confirmed, update status to confirmed
            console.log('Updating agreement status to confirmed...');
            await updateAgreementStatus(agreement_id!, 'confirmed');
            console.log('Agreement status updated to confirmed successfully');
            
            // Update local state to reflect the new status
            setAgreementStatus('confirmed');
            
            // Navigate directly to payment page instead of relying on StatusGuard
            console.log('Navigating to payment page...');
            navigate(`/${agreement_id}/payment`);
          } else {
            console.log('Agreement not in correct status for payment navigation');
          }
        } catch (error) {
          console.error('Error handling payment navigation:', error);
          // Fallback: try to update status and navigate
          try {
            await updateAgreementStatus(agreement_id!, 'confirmed');
            navigate(`/${agreement_id}/payment`);
          } catch (fallbackError) {
            console.error('Fallback navigation also failed:', fallbackError);
            // Still navigate to payment page as last resort
            navigate(`/${agreement_id}/payment`);
          }
        } finally {
          // Reset loading state
          setIsConfirming(false);
        }
      } else {
        // Show a message or alert that we're waiting for other signatures
        console.log("Waiting for all signatures to be completed");
      }
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleDownloadPdf = async () => {
    if (!agreement_id) return;
    
    try {
      await downloadAgreementPdf(agreement_id);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // You could add a toast notification here if you have one
    }
  };

  // Function to render text with bold formatting
  const renderTextWithBold = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.slice(2, -2);
        return <strong key={index} className="font-bold text-gray-900">{boldText}</strong>;
      }
      return part;
    });
  };

  // Handle scroll detection for terms and conditions
  const handleTermsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5; // 5px tolerance
    setHasScrolledToBottom(isAtBottom);
  };

  const signingParties = getSigningParties();

  return (
    <div className="space-y-8">
      {/* Agreement Header */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-black px-6 py-8 text-white">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">
              Search Property Engagement Agreement
            </h1>
            <p className="text-gray-300 text-sm sm:text-base">
              Please review all terms carefully before signing
            </p>
          </div>
        </div>
        
        <div className="p-6 bg-primary-50 border-b border-primary-200">
          <div className="flex items-center justify-center gap-3">
            <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-sm text-primary-800 font-medium">
              This is a legally binding agreement. All sections below must be reviewed and accepted.
            </p>
          </div>
        </div>
      </div>

      {/* Agreement Content */}
      <div className="text-gray-700 space-y-8">
        {/* Schedule Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Schedule</h3>
          </div>
          
          <div className="p-6">
            <div>
              {getProcessedScheduleItems().map((item, index) => {
                const nextItem = getProcessedScheduleItems()[index + 1];
                const shouldAddSpacing = !item.hasDynamicContent && (!nextItem || !nextItem.hasDynamicContent);
                
                return (
                  <div
                    key={item.id}
                    className={`pb-4 last:pb-0 ${
                      item.hasDynamicContent
                        ? 'bg-primary-50 -mx-3 px-3 py-4 rounded-lg border border-primary-200 mb-4'
                        : shouldAddSpacing
                          ? 'border-b border-gray-100 mb-4 last:border-b-0 last:mb-0'
                          : ''
                    }`}
                  >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
                        item.hasDynamicContent
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {item.id}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4>
                        {/* {item.hasDynamicContent && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            Personalized
                          </span>
                        )} */}
                      </div>
                      <div className="mt-1 text-gray-700 text-sm leading-relaxed">
                        {item.content.split('\n\n').map((paragraph, i) => {
                          // Handle bullet points and line breaks
                          const lines = paragraph.split('\n');
                          return (
                            <div key={i} className={i > 0 ? 'mt-2' : ''}>
                              {lines.map((line, lineIndex) => {
                                const trimmedLine = line.trim();
                                if (trimmedLine.match(/^(i{1,3}\.|\d+\.)/)) {
                                  // Handle numbered/roman numeral lists
                                  return (
                                    <div key={lineIndex} className="ml-4 mb-1">
                                      {renderTextWithBold(trimmedLine)}
                                    </div>
                                  );
                                } else if (trimmedLine.startsWith('•') || trimmedLine.match(/^[a-z]\)/)) {
                                  // Handle bullet points and lettered lists
                                  return (
                                    <div key={lineIndex} className="ml-6 mb-1">
                                      {renderTextWithBold(trimmedLine)}
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div key={lineIndex} className={lineIndex > 0 ? 'mt-1' : ''}>
                                      {renderTextWithBold(trimmedLine)}
                                    </div>
                                  );
                                }
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Terms and Conditions Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Terms and Conditions</h3>
          </div>
          
          <div className="relative">
            <div
              className="max-h-[400px] overflow-y-auto px-6 pt-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              onScroll={handleTermsScroll}
            >
              <div className="space-y-6 pb-6">
                {(shouldUseFollowOnData
                  ? getFollowOnTermsAndConditions()
                  : shouldUseCustomData
                    ? getCustomTermsAndConditions()
                    : termsAndConditions)
                  .filter((section) => {
                    // Only show "11. Correspondence" section for specific agreement ID or signature ID
                    if (section.title === "11. Correspondence") {
                      const allowedAgreementIds = ["58ab3720-b610-46b4-9272-a53c416b16ed"];
                      const allowedSignatureIds = ["f7efd69b-ff72-4643-865d-cbfcdf4cba87"];
                      
                      return (agreement_id && allowedAgreementIds.includes(agreement_id)) ||
                             (signatureId && allowedSignatureIds.includes(signatureId));
                    }
                    // Show all other sections for all agreements
                    return true;
                  })
                  .map((section, index) => (
                    <div key={index} className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                      <h5 className="font-semibold text-gray-900 mb-3 text-base">{section.title}</h5>
                      <div className="space-y-2">
                        {section.content.map((paragraph, pIndex) => (
                          <p key={pIndex} className="text-gray-700 text-sm leading-relaxed">{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 sticky bottom-0">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  checked={termsAccepted || agreementSigned || currentUserSigned}
                  disabled={currentUserSigned || agreementSigned || (!hasScrolledToBottom && !agreementSigned)}
                  onChange={() => hasScrolledToBottom && !agreementSigned && !currentUserSigned && setTermsAccepted(!termsAccepted)}
                  className={`h-5 w-5 border-gray-300 rounded mt-0.5 flex-shrink-0 transition-colors ${
                    currentUserSigned || agreementSigned
                      ? 'text-gray-400 cursor-not-allowed opacity-50'
                      : hasScrolledToBottom
                      ? 'text-primary-500 focus:ring-primary-500 cursor-pointer'
                      : 'text-gray-300 cursor-not-allowed opacity-50'
                  }`}
                  style={{
                    accentColor: currentUserSigned || agreementSigned ? '#d1d5db' : hasScrolledToBottom ? '#ffae00' : '#d1d5db' // gray for signed, primary-500 yellow for active, gray for inactive
                  }}
                />
                <div className="flex-1 flex items-start justify-between gap-3">
                  <label
                    htmlFor="termsAccepted"
                    className={`text-sm leading-relaxed font-medium transition-colors ${
                      currentUserSigned || agreementSigned
                        ? 'text-gray-500 cursor-not-allowed'
                        : hasScrolledToBottom
                        ? 'text-gray-700 cursor-pointer'
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {currentUserSigned || agreementSigned
                      ? "Terms and conditions have been accepted"
                      : hasScrolledToBottom
                      ? "I have read and agree to the terms and conditions outlined above"
                      : "Please scroll to the bottom to read all terms and conditions"
                    }
                  </label>
                  
                  {/* Attention Label - Only show when user needs to scroll */}
                  {!currentUserSigned && !agreementSigned && !hasScrolledToBottom && (
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                        <svg className="w-3 h-3 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Scroll Required
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Identity Verification Disclaimer */}
      {!agreementSigned && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-6">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="amlDisclaimerAccepted"
                checked={amlDisclaimerAccepted || currentUserSigned}
                disabled={currentUserSigned || agreementSigned}
                onChange={() => !currentUserSigned && !agreementSigned && setAmlDisclaimerAccepted(!amlDisclaimerAccepted)}
                className={`h-5 w-5 border-gray-300 rounded mt-0.5 flex-shrink-0 transition-colors ${
                  currentUserSigned || agreementSigned
                    ? 'text-gray-400 cursor-not-allowed opacity-50'
                    : 'text-primary-500 focus:ring-primary-500 cursor-pointer'
                }`}
                style={{
                  accentColor: currentUserSigned || agreementSigned ? '#d1d5db' : '#ffae00'
                }}
              />
              <label
                htmlFor="amlDisclaimerAccepted"
                className={`text-sm leading-relaxed font-medium italic transition-colors ${
                  currentUserSigned || agreementSigned
                    ? 'text-gray-500 cursor-not-allowed'
                    : 'text-gray-700 cursor-pointer'
                }`}
              >
                {currentUserSigned
                  ? 'Identity verification and due diligence obligations acknowledged'
                  : 'I acknowledge and agree that Search Property is required to conduct identity verification, source of funds checks, source of wealth checks and other due diligence as part of its anti-money laundering and counter-terrorism financing obligations.'}
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Signature Section */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">Digital Signatures</h3>
          <p className="text-sm text-gray-600 mt-1">
            {agreementSigned
              ? "All signatures for this agreement have been completed."
              : "Please sign below to execute this agreement."}
          </p>
        </div>

        <div className="p-6">
          {agreementSigned ? (
            <div className="my-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-green-900 mb-1">Agreement Fully Signed</h4>
                    <p className="text-sm text-green-800">
                      This agreement has been signed by all required parties.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : !currentUserSigned ? (
           <>
             {/* Primary Signer - Before Signing */}
             {isSecondaryView && secondarySignerProps ? (
               <div className="mb-6">
                 <SignatureCapture
                   name={secondarySignerProps.signerName}
                   onSignatureChange={secondarySignerProps.onSignatureChange}
                   signature={secondarySignerProps.signatureData}
                 />
                 
                 <div className="mt-6 flex justify-end">
                   <button
                     onClick={secondarySignerProps.onSubmitSignature}
                     disabled={!secondarySignerProps.signatureData || secondarySignerProps.submitting || !amlDisclaimerAccepted}
                     title={
                       !amlDisclaimerAccepted ? 'Please acknowledge the identity verification disclaimer' :
                       !secondarySignerProps.signatureData && !secondarySignerProps.submitting ? 'Please provide your signature' : ''
                     }
                     className={`px-6 py-2 rounded-lg text-white font-medium ${
                       !secondarySignerProps.signatureData || secondarySignerProps.submitting || !amlDisclaimerAccepted
                         ? 'bg-gray-400 cursor-not-allowed'
                         : 'bg-primary-500 hover:bg-primary-600'
                     } transition-colors`}
                   >
                     {secondarySignerProps.submitting ? (
                       <span className="flex items-center">
                         <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                         </svg>
                         Submitting...
                       </span>
                     ) : (
                       'Submit Signature'
                     )}
                   </button>
                 </div>
               </div>
             ) : signingParties.length > 0 && (
               <div className="mb-6">
                 <SignatureCapture
                   name={signingParties[0]}
                   onSignatureChange={(signature) => handleSignatureChange(signingParties[0], signature)}
                   signature={signatures[signingParties[0]]}
                 />
               </div>
             )}

             {/* Other Signers Status - Before Current User Signs */}
             {!isSecondaryView && signingParties.length > 1 && (
                <div className="mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-blue-900 mb-2">Additional Signers</h4>
                        <p className="text-sm text-blue-800 mb-3">
                          This agreement will be sent to the following parties for their signatures:
                        </p>
                        <div className="space-y-2">
                          {signingParties.slice(1).map((party, index) => (
                            <div key={party} className="flex items-center justify-between bg-white rounded px-3 py-2">
                              <span className="text-sm font-medium text-gray-900">{party}</span>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                Awaiting
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* After Current User Has Signed */}
              <div className="mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-green-900 mb-1">Your Signature Complete</h4>
                      <p className="text-sm text-green-800">
                        {isSecondaryView && secondarySignerProps ? (
                          <>You have successfully signed this agreement as: <strong>{secondarySignerProps.signerName}</strong></>
                        ) : (
                          <>You have successfully signed this agreement as: <strong>{signingParties[0]}</strong></>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Waiting for Other Signers */}
              {signingParties.length > 1 && (
                <div className="mb-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-yellow-900 mb-2">Waiting for Additional Signatures</h4>
                        <p className="text-sm text-yellow-800 mb-3">
                          <strong>The agreement will only be considered fully signed when all parties have signed.</strong> The agreement has been sent to the following parties. You will be notified when all signatures are complete.
                        </p>
                        <div className="space-y-2">
                          {signingParties.slice(1).map((party, index) => {
                            // Check if this signer has already signed by looking at individual details
                            const signerIndex = index + 1; // +1 because we're looking at slice(1), so index 0 = signer 2
                            const individualDetail = state.individualDetails[signerIndex];
                            const hasSigned = individualDetail?.has_signed || false;
                            
                            return (
                              <div key={party} className="flex items-center justify-between bg-white rounded px-3 py-2">
                                <span className="text-sm font-medium text-gray-900">{party}</span>
                                {hasSigned ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Signed
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Pending
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col gap-4">
            {/* Progress Indicator - Hide in secondary view */}
            {!isSecondaryView && (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Step 4 of 4 - Review & Sign
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Back button - Hide in secondary view */}
              {!isSecondaryView && (
                <button
                  onClick={onBack}
                  className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-all duration-200 text-center"
                >
                  Back to Details
                </button>
              )}
              
              {/* Download button - Hide in secondary view */}
              {!isSecondaryView && (
                <button
                  onClick={handleDownloadPdf}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-200"
                >
                  <Download className="w-4 h-4" />
                  Download Draft
                </button>
              )}
              
              {/* Hide Sign Agreement button in secondary view */}
              {!isSecondaryView && (
                <button
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting ||
                    isConfirming ||
                    (!currentUserSigned && !areAllSigned()) || // Disable "Sign Agreement" if not ready to sign
                    (currentUserSigned && !agreementSigned)    // Disable "Next" if not all signatures collected
                  }
                  title={
                    isSubmitting || isConfirming ? "" :
                    !currentUserSigned && !termsAccepted ? "Please accept the terms and conditions" :
                    !currentUserSigned && !amlDisclaimerAccepted ? "Please acknowledge the identity verification disclaimer" :
                    !currentUserSigned && !hasScrolledToBottom ? "Please scroll to the bottom of the terms and conditions" :
                    !currentUserSigned && (!signingParties.length || !signatures[signingParties[0]]) ? "Please provide your signature" :
                    currentUserSigned && !agreementSigned ? "Please wait for all parties to sign before proceeding" :
                    ""
                  }
                  className={`flex-1 sm:flex-2 px-8 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                    ((!currentUserSigned && areAllSigned()) || agreementSigned) && !isSubmitting && !isConfirming
                      ? 'bg-primary-500 hover:bg-primary-600 text-white hover:shadow-lg transform hover:scale-[1.02]'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting || isConfirming ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {isConfirming ? 'Confirming...' : currentUserSigned || agreementSigned ? 'Loading...' : 'Processing Signature...'}
                    </>
                  ) : currentUserSigned || agreementSigned ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      {/* Show different text based on agreement status */}
                      {agreementStatus === 'confirmed' ? 'Go to Payment' : 'Confirm and Continue'}
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" />
                      Sign Agreement
                    </>
                  )}
                </button>
              )}
            </div>
            
            {/* Status Message */}
            {!agreementSigned && (
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  {!hasScrolledToBottom
                    ? "Please scroll to the bottom of the terms and conditions"
                    : !termsAccepted
                    ? "Please accept the terms and conditions to continue"
                    : !amlDisclaimerAccepted
                    ? "Please acknowledge the identity verification disclaimer to continue"
                    : currentUserSigned
                    ? "Please wait for all parties to sign before proceeding"
                    : "Please provide your signature above to continue"
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}